import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import MaterialTable from "material-table";
import { IconButton, Tooltip, Typography, Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import Icon from "@jll/icon";
import {
  tableStyle,
  tableOptions,
  tableIcons,
} from "../../../../Component/Table/tableCommonStyles";

import styles from "./styles";
import { LongText } from "../../../../Component";
import CustomMTableHeader from "../../../../Component/Table/CustomMTableHeader";
import CustomMTableRow from "../../../../Component/Table/CustomTableRow";
import CustomMTableCell from "../../../../Component/Table/CustomMTableCell";
import CustomMTableActions from "../../../../Component/Table/CustomMTableActions";
import { ScoreIndicator } from "../../../../Component";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);

const relationTypes = {
  gup: "GUP",
  holdingCompanies: "Holding Companies",
  operatingSubs: "Operating Subs",
  specialPurposeVehicles: "Special Purpose Vehicles",
};

const RelatedEntitiesTable = ({
  data,
  canAddRelatedEntity,
  canRemoveRelatedEntity,
  onCreateNew,
  onRowDelete,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const filterData = (data) => {
    const gup = data.filter((x) => x.relation_type.toLowerCase() === "gup");
    const holdingCompanies = data.filter(
      (x) => x.relation_type.toLowerCase() === "holding companies",
    );
    const operatingSubs = data.filter(
      (x) => x.relation_type.toLowerCase() === "operating subs",
    );
    const specialPurposeVehicles = data.filter(
      (x) => x.relation_type.toLowerCase() === "special purpose vehicles",
    );

    return {
      gup,
      holdingCompanies,
      operatingSubs,
      specialPurposeVehicles,
    };
  };

  const getTableData = (data) => {
    return (
      data &&
      data.map((val) => {
        const kycScore =
          (data && data.find((s) => s.check_type === "KYC")) || {};
        return {
          ...val,
          source: val.related_local_entity_id
            ? t("labels.record.relatedEntities.table.values.local")
            : val.related_dnb_id
            ? t("labels.record.relatedEntities.table.values.dnb")
            : t("labels.record.relatedEntities.table.values.mdm"),
          kycScore: kycScore.accepted_score || kycScore.guided_score || "-",
          kycExpired: kycScore.expired,
        };
      })
    );
  };

  const buildTables = (
    data,
    relationType,
    relationTypeName,
    onRowDeleteClick,
    openCreateNewDialog,
    canAddRelatedEntity,
    canRemoveRelatedEntity,
  ) => {
    return (
      <div className={classes.tableWrapper} key={relationType}>
        <div className={classes.tableHeader}>
          <div>
            <Typography className={classes.tableTitle}>
              {relationTypeName}
            </Typography>
          </div>
          {canAddRelatedEntity &&
            ((relationType === "gup" && data.length <= 0) ||
              relationType !== "gup") && (
              <div className={classes.addButton}>
                <Button
                  color="primary"
                  variant="contained"
                  label={t("labels.common.search")}
                  style={{ height: 35 }}
                  onClick={() => openCreateNewDialog(relationType)}
                >
                  <AddIcon />
                </Button>
              </div>
            )}
        </div>

        <MaterialTable
          style={tableStyle}
          icons={tableIcons}
          options={tableOptions}
          isLoading={false}
          data={getTableData(data)}
          localization={{
            header: {
              actions: t("labels.common.table.actions"),
            },
            body: {
              emptyDataSourceMessage: t("labels.common.table.noRecordsFound"),
            },
          }}
          columns={[
            {
              title: t("labels.record.relatedEntities.table.headers.name"),
              field: "entity_name",
              render: (rowData) => {
                return <LongText text={rowData.entity_name} />;
              },
            },
            {
              title: t(
                "labels.record.relatedEntities.table.headers.addressLine1",
              ),
              field: "address_line_1",
              render: (rowData) => {
                return <LongText text={rowData.address_line_1} />;
              },
            },
            {
              title: t("labels.record.relatedEntities.table.headers.city"),
              field: "city",
            },
            {
              title: t("labels.record.relatedEntities.table.headers.postCode"),
              field: "postcode",
            },
            {
              title: t("labels.record.relatedEntities.table.headers.country"),
              field: "country_code",
            },
            {
              title: t("labels.record.relatedEntities.table.headers.source"),
              field: "source",
            },
            {
              title: t("labels.record.relatedEntities.table.headers.kycScore"),
              field: "kycScore",
              render: (rowData) => {
                return (
                  <ScoreIndicator
                    score={rowData.kycScore}
                    expired={rowData.kycExpired}
                  />
                );
              },
            },
          ]}
          actions={[
            {
              icon: "ViewColumn",
              tooltip: t("labels.common.view"),
              position: "row",
              onClick: () => {},
            },
            canRemoveRelatedEntity && {
              icon: "DeleteColumn",
              tooltip: t("labels.common.remove"),
              position: "row",
              onClick: (record) => onRowDeleteClick(record),
            },
          ]}
          components={{
            Actions: (props) => <CustomMTableActions {...props} />,
            Action: (props) => {
              switch (props.action.icon) {
                case "ViewColumn":
                  return props.data.related_local_entity_id ? (
                    <Tooltip title={props.action.tooltip} placement="bottom">
                      <IconButton
                        className={classes.actionIcons}
                        href={
                          "glc/records/viewRecord/basicinfo?id=" +
                          props.data.entity_id
                        }
                        disabled={!props.data.related_local_entity_id}
                        target="_blank"
                      >
                        <Icon
                          className="far fa-arrow-circle-right"
                          style={{ fontSize: 16 }}
                        />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    ""
                  );
                case "DeleteColumn":
                  return (
                    <Tooltip title={props.action.tooltip} placement="bottom">
                      <IconButton
                        className={classes.actionIcons}
                        onClick={(event) => props.action.onClick(props.data)}
                      >
                        <Icon
                          className="fas fa-trash"
                          style={{ fontSize: 16 }}
                        />
                      </IconButton>
                    </Tooltip>
                  );
                default:
                  return null;
              }
            },
            Header: (props) => <CustomMTableHeader {...props} />,
            Row: (props) => <CustomMTableRow {...props} />,
            Cell: (props) => <CustomMTableCell {...props} />,
          }}
        />
      </div>
    );
  };

  const groupedData = filterData(data);

  const tables = Object.keys(groupedData).map((sectionName) => {
    return buildTables(
      groupedData[sectionName],
      relationTypes[sectionName],
      t(`labels.record.relatedEntities.${sectionName}`),
      onRowDelete,
      onCreateNew,
      canAddRelatedEntity,
      canRemoveRelatedEntity,
    );
  });

  return tables;
};

export default RelatedEntitiesTable;
